import React, { useState, useRef, useEffect } from 'react';
import { FaDice } from "react-icons/fa";
import { IoSettingsOutline } from "react-icons/io5";
import { CiMoneyCheck1 } from "react-icons/ci";
import { TbPrompt } from "react-icons/tb";
import { IoIosArrowDown } from "react-icons/io";

const GenerationSettings = ({ generationSettings, onSettingsChange, selectedPrompt, setShowPromptGalleryModal, isHidden, setIsHidden }) => {
    const [showSettings, setShowSettings] = useState(false);
    const [settings, setSettings] = useState(generationSettings);
    const settingsRef = useRef(null);

    useEffect(() => {
        if (selectedPrompt) {
            console.log("selectedPrompt", selectedPrompt);
            handleSettingChange('prompt', selectedPrompt);
        }
    }, [selectedPrompt]);

    const aspectRatios = [
        { value: 'default', label: 'Default', width: 1, height: 1 },
        { value: '16:9', label: '16:9', width: 16, height: 9 },
        { value: '9:16', label: '9:16', width: 9, height: 16 },
        { value: '1:1', label: '1:1', width: 1, height: 1 },
        { value: '4:3', label: '4:3', width: 4, height: 3 },
        { value: '3:4', label: '3:4', width: 3, height: 4 }
    ];

    // Add click outside handler for settings dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target)) {
                setShowSettings(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle settings change and notify parent
    const handleSettingChange = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        setSettings(newSettings);
        onSettingsChange?.(newSettings);
    };

    const generateRandomSeed = () => {
        handleSettingChange('seed', Math.floor(Math.random() * 1000000));
    };

    return (
        <div className="relative" ref={settingsRef}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="flex items-center gap-2 px-2.5 py-1 text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors duration-200"
                    >
                        <IoSettingsOutline className="w-4 h-4 text-primary-600" />
                        Settings
                    </button>

                    <div className="flex items-center justify-center gap-2 text-xs font-medium text-stone-600 bg-stone-100/50 rounded-xl px-2 py-1 shadow-sm">
                        <CiMoneyCheck1 className="w-4 h-4 text-stone-600" />
                        <div className="flex items-center gap-1">
                            <span className="font-semibold">{settings.model === 'flux-kontext-pro' ? '5' : '10'}</span>
                            <span className="text-stone-600">credits required</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div 
                        className="flex items-center gap-2 px-2 py-1 text-xs font-medium bg-primary-50 hover:bg-primary-100 text-primary-600 rounded-xl cursor-pointer transition-all duration-200 border border-primary-200 hover:border-primary-300 hover:shadow-sm group" 
                        onClick={() => setShowPromptGalleryModal(true)}
                    >
                        <TbPrompt className="w-4 h-4 text-primary-500 group-hover:scale-110 transition-transform" />
                        <span className="tracking-wide">
                            Prompt Gallery
                        </span>
                    </div>

                    <button
                        onClick={() => setIsHidden(!isHidden)}
                        className="flex items-center justify-center py-1 px-2 text-primary-500 bg-primary-50 hover:bg-primary-100 border border-primary-200 hover:border-primary-300 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                    >
                        <div className="flex items-center justify-center gap-1">
                            <div className="text-xs font-medium text-primary-500">Hide</div>
                            <IoIosArrowDown className="w-4 h-4 text-primary-500" />
                        </div>   
                    </button>
                </div>
            </div>
            
            {showSettings && (
                <div className="absolute bottom-full left-0 mb-1 w-[400px] overflow-y-auto bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200/80 py-2 z-[100]">
                    <div className="px-4 pb-4 space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                Select Model
                            </label>
                            <div className="grid grid-cols-2 gap-1.5">
                                <button
                                    onClick={() => handleSettingChange('model', 'flux-kontext-pro')}
                                    className={`relative p-2.5 rounded-lg border transition-all duration-200 ${
                                        settings.model === 'flux-kontext-pro'
                                            ? 'border-primary-500 bg-primary-50/80 shadow-sm'
                                            : 'border-gray-200 hover:border-primary-200 hover:bg-primary-50/50'
                                    }`}
                                >
                                    <div className="space-y-1.5">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-gray-900">FLUX Kontext Pro</span>
                                            <span className="text-xs text-gray-500 mt-0.5">Balanced performance</span>
                                        </div>
                                        <div className="flex items-center justify-center gap-1.5 text-[10px] font-medium text-rose-600 bg-rose-50/80 px-2 py-1 rounded-full">
                                            <CiMoneyCheck1 className="w-3.5 h-3.5" />
                                            <span className="tracking-wide">5 credits</span>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => handleSettingChange('model', 'flux-kontext-max')}
                                    className={`relative p-2.5 rounded-lg border transition-all duration-200 ${
                                        settings.model === 'flux-kontext-max'
                                            ? 'border-primary-500 bg-primary-50/80 shadow-sm'
                                            : 'border-gray-200 hover:border-primary-200 hover:bg-primary-50/50'
                                    }`}
                                >
                                    <div className="space-y-1.5">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-gray-900">FLUX Kontext Max</span>
                                            <span className="text-xs text-gray-500 mt-0.5">Maximum quality</span>
                                        </div>
                                        <div className="flex items-center justify-center gap-1.5 text-[10px] font-medium text-rose-600 bg-rose-50/80 px-2 py-1 rounded-full">
                                            <CiMoneyCheck1 className="w-3.5 h-3.5" />
                                            <span className="tracking-wide">10 credits</span>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                Aspect Ratio
                            </label>
                            <div className="grid grid-cols-6 gap-1.5">
                                {aspectRatios.map((ratio) => (
                                    <button
                                        key={ratio.value}
                                        onClick={() => handleSettingChange('aspect_ratio', ratio.value)}
                                        className={`relative group p-1.5 rounded-lg border transition-all duration-200 ${
                                            settings.aspect_ratio === ratio.value
                                                ? 'border-primary-500 bg-primary-50/80 shadow-sm'
                                                : 'border-gray-200 hover:border-primary-200 hover:bg-primary-50/50'
                                        }`}
                                    >
                                        <div 
                                            className="w-full bg-gray-200/80 rounded-md transition-colors duration-200"
                                            style={{
                                                paddingBottom: `${(ratio.height / ratio.width) * 100}%`
                                            }}
                                        />
                                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-gray-600">
                                            {ratio.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-3 py-1">
                            <input
                                type="checkbox"
                                id="prompt_upsampling"
                                checked={settings.prompt_upsampling}
                                onChange={(e) => handleSettingChange('prompt_upsampling', e.target.checked)}
                                className="w-4 h-4 text-primary-500 rounded border-gray-300 focus:ring-primary-500/20"
                            />
                            <label htmlFor="prompt_upsampling" className="text-sm font-medium text-gray-700">
                                Enable prompt upsampling
                            </label>
                        </div>

                        <div className="mb-2">
                            <label className="text-sm font-medium text-gray-700">Output Format</label>
                            <div className="flex items-center p-0.5 bg-gray-100/80 rounded-lg">
                                <button
                                    onClick={() => handleSettingChange('output_format', 'jpeg')}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                                        settings.output_format === 'jpeg'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    JPEG
                                </button>
                                <button
                                    onClick={() => handleSettingChange('output_format', 'png')}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                                        settings.output_format === 'png'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    PNG
                                </button>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700 flex justify-between items-center">
                                <span className="flex items-center gap-1.5">
                                    Seed
                                </span>
                                <span className="text-gray-500 font-normal">{settings.seed}</span>
                            </label>
                            <div className="flex gap-1.5">
                                <input
                                    type="number"
                                    value={settings.seed}
                                    onChange={(e) => handleSettingChange('seed', parseInt(e.target.value) || 42)}
                                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-xs focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 outline-none shadow-sm"
                                />
                                <button 
                                    onClick={generateRandomSeed}
                                    className="px-2 py-1.5 rounded-lg border border-gray-200 hover:bg-primary-50 hover:border-primary-200 transition-colors duration-200"
                                    title="Generate random seed"
                                >
                                    <FaDice className="w-3.5 h-3.5 text-gray-500" />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700 flex justify-between items-center">
                                <span className="flex items-center gap-1.5">
                                    Safety Tolerance
                                </span>
                                <span className="text-gray-500 font-normal">{settings.safety_tolerance.toFixed(1)}</span>
                            </label>
                            <input
                                type="range"
                                min="2"
                                max="6"
                                step="0.1"
                                value={settings.safety_tolerance}
                                onChange={(e) => handleSettingChange('safety_tolerance', parseFloat(e.target.value))}
                                className="w-full h-1.5 bg-gray-200/80 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-primary-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-primary-500 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GenerationSettings; 